import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Trash2, Database, Cookie, Globe, Scale, ShieldCheck, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';
import { Seo } from '@/components/Seo';
import { AutoTranslate } from '@/components/AutoTranslate';
import { MiniLanguageToggle } from '@/components/MiniLanguageToggle';
import DeletionStatusTracker, { DeletionStatusTimeline } from '@/components/DeletionStatusTracker';

const PrivacyPolicyPage = () => {
  const lastUpdated = "May 22, 2026";
  const [deletionForm, setDeletionForm] = useState({
    email: '',
    reason: '',
    confirmText: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [submittedRequestId, setSubmittedRequestId] = useState(null);
  const [submittedRecord, setSubmittedRecord] = useState(null);

  const handleDeletionRequest = async (e) => {
    e.preventDefault();
    
    if (deletionForm.confirmText.toLowerCase() !== 'delete my data') {
      toast.error('Please type "DELETE MY DATA" to confirm');
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await apiFetch('/api/privacy/deletion-request', {
        method: 'POST',
        body: {
          email: deletionForm.email,
          reason: deletionForm.reason
        },
      });

      if (error) {
        throw new Error('Failed to submit request');
      }

      setRequestSubmitted(true);
      const newId = data?.request_id || null;
      setSubmittedRequestId(newId);
      // Immediately fetch the freshly-created record so we can show the
      // timeline (phase=received) instead of just a confirmation message.
      if (newId) {
        try {
          const { data: statusData } = await apiFetch(
            `/api/privacy/deletion-status/${encodeURIComponent(newId)}`
          );
          if (statusData) setSubmittedRecord(statusData);
        } catch (_) {
          /* timeline will fall back to the static success copy */
        }
      }
      toast.success('Data deletion request submitted successfully');
    } catch (error) {
      console.error('Deletion request error:', error);
      toast.error('Failed to submit request. Please email us directly at clearcommunicationapp@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Seo
        path="/privacy"
        title="Privacy Policy — ClearCommunicationApp"
        description="How ClearCommunicationApp collects, uses, protects, and deletes your personal data — including the specific Google User Data Disclosure required for Google OAuth verification."
      />
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to App</span>
            </Link>
            <MiniLanguageToggle className="ml-auto" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <AutoTranslate>
        <div className="space-y-8">
          {/* Title */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
          </div>

          {/* Introduction */}
          <section className="prose dark:prose-invert max-w-none">
            <p className="text-lg leading-relaxed">
              ClearCommunicationApp ("we", "our", or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, and safeguard your information 
              when you use our application and services.
            </p>
          </section>

          {/* No Selling/Sharing Highlight */}
          <section className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-6 border-2 border-green-500/30">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-500/20">
                <ShieldCheck className="h-8 w-8 text-green-500" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-green-700 dark:text-green-400">We Do NOT Sell or Share Your Data</h2>
                <p className="text-muted-foreground mt-1">
                  Your personal information is never sold, rented, or shared with third parties for marketing purposes. 
                  All collected data is stored securely and used solely for analytics and account functionality.
                </p>
              </div>
            </div>
          </section>

          {/* Sections */}
          <div className="space-y-8">
            {/* Information We Collect */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Database className="h-6 w-6 text-primary" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">Information We Collect</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p><strong className="text-foreground">Account Information:</strong> When you create an account, we collect your email address, name, and password (encrypted).</p>
                    <p><strong className="text-foreground">Social Login Data:</strong> If you sign in with Google or Facebook, we receive your name, email, and profile picture from those services.</p>
                    <p><strong className="text-foreground">Usage Data:</strong> We collect information about how you use the app, including phrases viewed, favorites, learning progress, and streaks.</p>
                    <p><strong className="text-foreground">Device Information:</strong> We may collect device type, operating system, and browser type for analytics purposes.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Globe className="h-6 w-6 text-blue-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">How We Use Your Information</h2>
                  <ul className="space-y-2 text-muted-foreground list-disc list-inside">
                    <li>To provide and maintain our service</li>
                    <li>To personalize your learning experience</li>
                    <li>To track your progress and maintain streaks</li>
                    <li>To send you important updates about the service</li>
                    <li>To respond to your inquiries and support requests</li>
                    <li>To improve our app and develop new features</li>
                    <li>To detect and prevent fraud or abuse</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* === Google User Data Disclosure (OAuth verification compliance) === */}
            <section
              id="google-user-data"
              className="bg-card rounded-xl p-6 border-2 border-blue-500/30"
              data-testid="google-user-data-section"
            >
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <div className="space-y-4 flex-1">
                  <h2 className="text-2xl font-semibold">Google User Data — Specific Disclosure</h2>
                  <p className="text-sm text-muted-foreground">
                    This section satisfies the Google API Services User Data Policy and the Google APIs Terms of Service.
                    It applies only if you sign in with Google.
                  </p>

                  <div className="space-y-3 text-muted-foreground">
                    <div>
                      <p className="font-semibold text-foreground mb-1">1. Data Accessed</p>
                      <p>
                        When you choose "Sign in with Google", we request the OAuth scopes
                        <code className="mx-1 px-1.5 py-0.5 bg-muted rounded text-xs">openid</code>,
                        <code className="mx-1 px-1.5 py-0.5 bg-muted rounded text-xs">email</code>, and
                        <code className="mx-1 px-1.5 py-0.5 bg-muted rounded text-xs">profile</code>.
                        From these scopes we receive your <strong>Google account email address</strong>,
                        <strong> full name</strong>, <strong>locale</strong>, and <strong>profile picture URL</strong>.
                        We do <strong>not</strong> request access to Gmail, Drive, Calendar, Contacts, YouTube, or any
                        restricted/sensitive Google API scope.
                      </p>
                    </div>

                    <div>
                      <p className="font-semibold text-foreground mb-1">2. Data Usage</p>
                      <p>
                        We use the data above to (a) create or look up your ClearCommunicationApp account,
                        (b) display your profile picture and name inside the app, and (c) send transactional
                        email notifications you opt in to (welcome email, password reset, weekly digest).
                        We do <strong>not</strong> sell, rent, or share Google user data for advertising,
                        profiling, or any purpose unrelated to providing the ClearCommunicationApp service.
                        We do <strong>not</strong> use Google user data to train any AI or machine-learning model.
                      </p>
                    </div>

                    <div>
                      <p className="font-semibold text-foreground mb-1">3. Data Sharing</p>
                      <p>
                        Google user data is <strong>not</strong> shared with any third party except the
                        infrastructure providers necessary to operate the service: MongoDB Atlas (encrypted
                        database storage), Resend (transactional email delivery), and Cloudflare (CDN +
                        DDoS protection). Each provider is contractually bound to use the data only to
                        deliver the service to us and not for any independent purpose. We never share
                        Google user data with data brokers, advertisers, or marketing networks.
                      </p>
                    </div>

                    <div>
                      <p className="font-semibold text-foreground mb-1">4. Data Storage &amp; Protection</p>
                      <p>
                        Data is stored on MongoDB Atlas with AES-256 encryption at rest and TLS 1.3 in
                        transit. Passwords (for non-Google accounts) are hashed with bcrypt at cost 12.
                        Session tokens are stored in <strong>httpOnly, Secure, SameSite=Lax</strong>
                        cookies so they cannot be read by client-side JavaScript. Backups are
                        encrypted, snapshotted daily at 03:00 UTC, and retained for 30 days.
                        Server access is limited to two named administrators with two-factor
                        authentication enforced.
                      </p>
                    </div>

                    <div>
                      <p className="font-semibold text-foreground mb-1">5. Data Retention &amp; Deletion</p>
                      <p>
                        We retain your Google-derived profile data for as long as your ClearCommunicationApp
                        account is active. You can delete your account at any time from the
                        <strong> Account → Delete account</strong> screen inside the app, or by submitting
                        the form below. Account deletion permanently removes all Google user data we hold
                        about you within 30 days, including from backups. We retain only the minimum
                        information required to comply with legal obligations (e.g. subscription payment
                        records for tax purposes, hashed user IDs in anti-abuse logs).
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground pt-2 border-t border-border">
                    Questions about this disclosure? Email
                    <a href="mailto:clearcommunicationapp@gmail.com" className="text-primary underline ml-1">
                      clearcommunicationapp@gmail.com
                    </a>
                    .
                  </p>
                </div>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Scale className="h-6 w-6 text-green-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">Data Protection & Third Parties</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20 mb-4">
                      <p className="font-semibold text-green-700 dark:text-green-400">Our Commitment to Your Privacy:</p>
                      <ul className="list-disc list-inside space-y-1 mt-2">
                        <li><strong className="text-foreground">We NEVER sell your personal information</strong></li>
                        <li><strong className="text-foreground">We NEVER share your account data with data brokers</strong></li>
                        <li><strong className="text-foreground">Paid VIP subscribers see NO ads at all</strong></li>
                      </ul>
                    </div>
                    <p>Your account data is used for:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong className="text-foreground">Account Management:</strong> Keeping you logged in and saving your progress</li>
                      <li><strong className="text-foreground">Analytics:</strong> Understanding how to improve the app (anonymized)</li>
                      <li><strong className="text-foreground">Essential Services:</strong> Processing payments (Stripe), sending account emails (Resend), push notifications (OneSignal/Firebase)</li>
                    </ul>

                    <div className="mt-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <p className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Google AdSense (free / guest users only):</p>
                      <p className="text-sm mb-2">
                        For users on the free tier and guests, we display advertisements served by Google
                        AdSense and may also use Google ad-blocking-recovery messages. Google and its
                        third-party advertising partners may use cookies, web beacons, and similar
                        technologies to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Serve ads based on your prior visits to this site or other sites on the
                            internet (interest-based / personalized advertising)</li>
                        <li>Measure ad performance and prevent fraud</li>
                        <li>Identify which ads are most effective</li>
                      </ul>
                      <p className="text-sm mt-2">
                        Google's use of advertising cookies enables it and its partners to serve ads to you
                        based on your visit to our site and/or other sites on the Internet. You can opt out
                        of personalized advertising at any time by visiting{' '}
                        <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Ads Settings</a>.
                        For EU/EEA/UK users, our consent banner powered by Google's Funding Choices lets
                        you accept or refuse personalised ads in compliance with the GDPR / UK GDPR /
                        Digital Services Act.
                      </p>
                      <p className="text-sm mt-2">
                        For more information about how Google uses data when you use our partners' sites
                        or apps, see Google's{' '}
                        <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-primary underline">privacy and terms</a>.
                      </p>
                    </div>

                    <p className="mt-3 text-sm">All data is stored securely with encryption and access controls.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Cookies */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Cookie className="h-6 w-6 text-yellow-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">Cookies & Tracking</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>We use cookies and similar technologies to:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Keep you logged in</li>
                      <li>Remember your preferences</li>
                      <li>Analyze app usage with Google Analytics</li>
                      <li><strong className="text-foreground">Serve and measure Google AdSense advertisements (free / guest users only)</strong></li>
                      <li>Detect and prevent fraudulent / automated traffic</li>
                      <li>Improve our services</li>
                    </ul>
                    <p>
                      Third-party vendors, including Google, use cookies to serve ads based on your
                      prior visits to this site or other sites on the internet. You can opt out of
                      personalized advertising any time at{' '}
                      <a href="https://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer" className="text-primary underline">aboutads.info/choices</a>
                      {' '}or{' '}
                      <a href="https://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer" className="text-primary underline">youronlinechoices.eu</a>.
                      You can also control all cookies through your browser settings.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Data Retention & Deletion */}
            <section className="bg-card rounded-xl p-6 border border-border" id="data-deletion">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <Trash2 className="h-6 w-6 text-red-500" />
                </div>
                <div className="space-y-4 flex-1">
                  <h2 className="text-2xl font-semibold">Data Retention & Deletion</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>We retain your data for as long as your account is active. You have the right to:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong className="text-foreground">Access your data:</strong> Request a copy of your personal information</li>
                      <li><strong className="text-foreground">Correct your data:</strong> Update inaccurate information</li>
                      <li><strong className="text-foreground">Delete your data:</strong> Request permanent deletion of your account and data</li>
                    </ul>
                    
                    {/* Manual Deletion Instructions */}
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
                      <h3 className="font-semibold text-foreground mb-2">Option 1: Delete Through the App</h3>
                      <ol className="list-decimal list-inside space-y-1 text-sm">
                        <li>Log into your ClearCommunicationApp account</li>
                        <li>Go to Settings (gear icon in the menu)</li>
                        <li>Scroll to "Delete Account" section</li>
                        <li>Click "Delete My Account" and confirm</li>
                        <li>Your data will be permanently deleted within 30 days</li>
                      </ol>
                    </div>

                    {/* Data Deletion Request Form */}
                    <div className="mt-4 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                      <h3 className="font-semibold text-foreground mb-3">Option 2: Submit a Data Deletion Request</h3>
                      
                      {requestSubmitted ? (
                        <div className="py-4 space-y-4" data-testid="deletion-request-success">
                          <div className="text-center">
                            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                            <h4 className="text-lg font-semibold text-green-700 dark:text-green-400">Request Submitted Successfully</h4>
                            <p className="text-muted-foreground mt-2 text-sm">
                              We've received your data deletion request. You'll receive a confirmation email at the address provided.
                            </p>
                            {submittedRequestId && (
                              <p
                                className="mt-3 text-xs text-muted-foreground"
                                data-testid="deletion-request-id"
                              >
                                Your request ID:{' '}
                                <code className="font-mono bg-background px-2 py-0.5 rounded border">
                                  {submittedRequestId}
                                </code>
                                <br />
                                <span className="text-[11px]">
                                  Save this — you can come back here anytime to track progress.
                                </span>
                              </p>
                            )}
                          </div>
                          {submittedRecord && (
                            <div className="bg-background p-4 rounded-lg border">
                              <DeletionStatusTimeline record={submittedRecord} />
                            </div>
                          )}
                        </div>
                      ) : (
                        <form onSubmit={handleDeletionRequest} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                              Email Address *
                            </label>
                            <Input
                              type="email"
                              placeholder="Enter the email associated with your account"
                              value={deletionForm.email}
                              onChange={(e) => setDeletionForm({...deletionForm, email: e.target.value})}
                              required
                              className="bg-background"
                              data-testid="deletion-email-input"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                              Reason for Deletion (Optional)
                            </label>
                            <Textarea
                              placeholder="Help us improve - why are you leaving?"
                              value={deletionForm.reason}
                              onChange={(e) => setDeletionForm({...deletionForm, reason: e.target.value})}
                              className="bg-background resize-none"
                              rows={3}
                              data-testid="deletion-reason-input"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                              Type "DELETE MY DATA" to confirm *
                            </label>
                            <Input
                              type="text"
                              placeholder="DELETE MY DATA"
                              value={deletionForm.confirmText}
                              onChange={(e) => setDeletionForm({...deletionForm, confirmText: e.target.value})}
                              required
                              className="bg-background"
                              data-testid="deletion-confirm-input"
                            />
                          </div>
                          
                          <Button
                            type="submit"
                            variant="destructive"
                            className="w-full"
                            disabled={isSubmitting}
                            data-testid="deletion-submit-btn"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Submitting Request...
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-2" />
                                Submit Deletion Request
                              </>
                            )}
                          </Button>
                          
                          <p className="text-xs text-muted-foreground text-center">
                            By submitting, you understand that all your account data will be permanently deleted within 30 days.
                          </p>
                        </form>
                      )}
                    </div>

                    <p className="mt-3 text-sm">
                      You can also email us directly at <a href="mailto:clearcommunicationapp@gmail.com" className="text-primary hover:underline">clearcommunicationapp@gmail.com</a> with subject "Delete My Account"
                    </p>

                    {/* Existing-request lookup widget */}
                    <div className="mt-6">
                      <DeletionStatusTracker />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Mail className="h-6 w-6 text-purple-500" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold">Contact Us</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p>If you have questions about this Privacy Policy or your data, contact us:</p>
                    <ul className="space-y-2">
                      <li><strong className="text-foreground">Email:</strong> <a href="mailto:clearcommunicationapp@gmail.com" className="text-primary hover:underline">clearcommunicationapp@gmail.com</a></li>
                      <li><strong className="text-foreground">Website:</strong> <a href="https://clearcommunicationapp.com" className="text-primary hover:underline">www.clearcommunicationapp.com</a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Changes */}
            <section className="bg-muted/50 rounded-xl p-6">
              <h2 className="text-xl font-semibold mb-3">Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last updated" date. 
                Continued use of the app after changes constitutes acceptance of the updated policy.
              </p>
            </section>
          </div>

          {/* Footer Links */}
          <div className="flex flex-wrap gap-4 justify-center pt-8 border-t border-border">
            <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
            <span className="text-muted-foreground">|</span>
            <Link to="/" className="text-primary hover:underline">Return to App</Link>
          </div>
        </div>
        </AutoTranslate>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
